"use client";
import { useTranslations } from "next-intl";
import * as dc from "@/components/layout/list/defaultType";
import { Container, DataList, Flex, Skeleton } from "@radix-ui/themes";
import { Datum, LegendProps } from "@nivo/legends";
import type { DataElement, DataInputCommonParams, DataInputParams, FlavorWheelProp, InputTypeData, RadarProps } from "./inputType";
import { Form, Skeleton as AntSkeleton, Radio, Select, FormInstance, Button } from "antd";
import { Debug } from "@/util/debug";
import { checkNoItem, GetDataInputElement, runFormFunction } from "./inputFunction";
import { TransitionType } from "@/components/layout/layoutType";
import { GridLabelComponent, RadarSliceTooltipProps } from "@nivo/radar";
import DataInput, { GetDataLabel } from "../data/dataInput";
import { CSSProperties, Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getDataKeyLabel, getDataKeyList, getDataKeyName, getDataKeyValue } from "@/util/datautil";
import { getStringArr, getStringLabel, getStringRecord } from "@/util/arrayutil";
import { PropertyAccessor } from "@nivo/core";
import { cn } from "@/lib/utils";
import { nivoTheme } from "../list/defaultType";
import { ComputedDatum, ResponsiveSunburst, SunburstCustomLayer, SunburstCustomLayerProps } from "@nivo/sunburst";
import { checkMobile } from "@/util/checkMobile";
import { animated, useSpring } from "@react-spring/web";

import { checkEmpty } from "@/util/stringutil";
import { format } from "path";
import usePrevious from "@/app/hook/usePrevious";
import { checkSafari, fileSaved, fileSaveError } from "@/util/apputil";
import { enLocale, koLocale } from "@/i18n/routing";
import { AlertContext } from "@/components/context/alertContext";
import { exportChartAsPNG } from "@/util/fileutil";
import dayjs from "dayjs";
import Icon from "@/components/common/icon/icon";
// import dynamic from "next/dynamic";
// const ResponsiveSunburst = dynamic(() => import("@nivo/sunburst").then((mod) => mod.ResponsiveSunburst), { ssr: false });

export const dataConverter = (t: TransitionType, params: DataInputParams, ids: string[]): Record<string, unknown> => {
  const target = params.flavorWheelProp?.target ?? params.name;
  if (target && params.codeGroup) {
    const checkReverse =
      typeof params.flavorWheelProp?.checkReverse === "string"
        ? params.form?.getFieldInstance(params.flavorWheelProp.checkReverse)
          ? params.form.getFieldValue(params.flavorWheelProp.checkReverse)
          : params.values[params.flavorWheelProp.checkReverse]
        : params.flavorWheelProp?.checkReverse;
    const check_values = params.form?.getFieldInstance(target)
      ? params.form?.getFieldValue(target)
      : (params.values[target] ?? "").split(",");
    // Debug(check_values);
    const check_value_arr = check_values instanceof Array ? check_values : [];
    const codeList = params.codeGroups && params.codeGroups[params.codeGroup] ? params.codeGroups[params.codeGroup] : undefined;
    if (codeList !== undefined) {
      const result: Record<string, any> = { name: params.codeGroup, children: [] };
      const resultMap: Record<string, any> = {};
      const result_children: any[] = result.children;
      const showIds: string[] = [];
      if (result_children) {
        const level1 = codeList.filter((item) => {
          return item.code_filter === undefined;
        });
        const level1_filter = level1.map((item) => {
          const check = check_value_arr.includes(item.code_id);
          const temp_result = {
            id: item.code_id,
            name: item.code_id,
            label: params.locale !== koLocale ? item.code_name : item.code_name_kr,
            subLabel: params.locale === enLocale ? undefined : item.code_name,
            color: item.code_alias1,
            check: checkReverse ? !check : check,
            subCheck: false,
            parentNode: undefined,
            level: 1,
            children: [],
          };
          result_children.push(temp_result);
          resultMap[item.code_id] = { count: 0, result: temp_result, children: temp_result.children };
          return item.code_id;
        });
        const level2 = codeList.filter((item) => level1_filter.includes(item.code_filter));
        const level2_filter = level2.map((item) => {
          const parent_name = item.code_filter;
          if (parent_name) {
            const check = check_value_arr.includes(item.code_id);
            const temp_result = {
              id: item.code_id,
              name: item.code_id,
              label: params.locale !== koLocale ? item.code_name : item.code_name_kr,
              subLabel: params.locale === enLocale ? undefined : item.code_name,
              color: item.code_alias1,
              check: checkReverse ? !check : check,
              subCheck: false,
              parentNode: item.code_filter,
              level: 2,
              children: [],
            };
            const parent_result = resultMap[parent_name];
            parent_result.children.push(temp_result);
            if (parent_result) {
              if (check) {
                parent_result.result.subCheck = true;
              }
            }
            resultMap[item.code_id] = { count: 0, result: temp_result, children: temp_result.children };
          }
          return item.code_id;
        });
        const level3 = codeList.filter((item) => level2_filter.includes(item.code_filter));
        const level3_filter = level3.map((item) => {
          const parent_name = item.code_filter;
          if (parent_name) {
            const check = check_value_arr.includes(item.code_id);
            const temp_result = {
              id: item.code_id,
              name: item.code_id,
              label: params.locale !== koLocale ? item.code_name : item.code_name_kr,
              subLabel: params.locale === enLocale ? undefined : item.code_name,
              color: item.code_alias1,
              check: checkReverse ? !check : check,
              subCheck: false,
              parentNode: item.code_filter,
              level: 3,
              children: [],
            };
            const parent_result = resultMap[parent_name];
            if (parent_result) {
              parent_result.children.push(temp_result);
              if (check) {
                parent_result.result.subCheck = true;
                const sub_parent_result = parent_result.result.parentNode ? resultMap[parent_result.result.parentNode] : undefined;
                if (sub_parent_result) {
                  sub_parent_result.result.subCheck = true;
                }
              }
            }
            resultMap[item.code_id] = { count: 0, result: temp_result, children: temp_result.children };
          }
          return item.code_id;
        });
        const showSimple =
          typeof params.flavorWheelProp?.showSimple === "string"
            ? params.form?.getFieldInstance(params.flavorWheelProp.showSimple)
              ? params.form.getFieldValue(params.flavorWheelProp.showSimple)
              : params.values[params.flavorWheelProp.showSimple]
            : params.flavorWheelProp?.showSimple;
        if (showSimple) {
          //간단히 보기 1차 처리
          if (showSimple && !checkEmpty(check_value_arr) && result.children) {
            let last_index = -1;
            const change_children: any[] = result.children.filter((item: any, index: number) => {
              const check = item.check || item.subCheck;
              if (check) {
                last_index = index;
              }
              return check;
            });
            if (change_children.length > 0) {
              if (last_index !== -1) {
                // Debug(last_index, result.children);
                if (change_children.length === 1) {
                  if (last_index - 1 < 0) {
                    // change_children.push(result.children[result.children.length - 1]);
                  } else {
                    // change_children.push(result.children[last_index - 1]);
                  }
                  // if (last_index + 1 < result.children.length - 1) {
                  //   change_children.push(result.children[last_index + 1]);
                  // } else {
                  //   change_children.push(result.children[0]);
                  // }
                  // result.children
                } else if (change_children.length === 2) {
                  // if (last_index + 1 < result.children.length - 1) {
                  //   change_children.push(result.children[last_index - 1]);
                  // } else {
                  //   change_children.push(result.children[0]);
                  // }
                }
              }
              if (change_children.length >= 1) {
                change_children.forEach((item) => {
                  // const mainCheck = item.check || item.subCheck;
                  if (item.check) {
                  } else if (item.subCheck) {
                    const filter = item.children.filter((sub_item: any) => {
                      return sub_item.check || sub_item.subCheck;
                    });
                    item.children = filter;
                    //2차 필터링 시작
                    if (!checkEmpty(item.children)) {
                      item.children.forEach((sub_item: any) => {
                        if (!sub_item.check && sub_item.subCheck) {
                          const sub_filter = sub_item.children.filter((sub_item_child: any) => {
                            return sub_item_child.check || sub_item_child.subCheck;
                          });
                          sub_item.children = sub_filter;
                        }
                      });
                    }
                  } else {
                    if (
                      (item.children && item.children[0] && item.children[0].children && item.children[0].children.length > 0) ||
                      item.children.length < 2
                    ) {
                      // item.children = [item.children[0]];
                    } else {
                      // item.children = [item.children[0], item.children[1]];
                    }
                  }
                });
                result.children = change_children;
              }
            }
          }
        }
        Object.keys(resultMap).forEach((key) => {
          const item = resultMap[key];
          if (item && item.result && item.children && item.children.length === 0) {
            item.result["loc"] = 1;
            delete item.result.children;
          }
        });
        if (showSimple && result.children) {
          result.children.forEach((item: any) => {
            showIds.push(item.id);
            if (!checkEmpty(item.children)) {
              item.children.forEach((sub_item: any) => {
                showIds.push(sub_item.id);
                if (!checkEmpty(sub_item.children)) {
                  sub_item.children.forEach((sub_item_children: any) => {
                    showIds.push(sub_item_children.id);
                  });
                }
              });
            }
          });
          showIds.sort();
          const ids_same = showIds.every((show_item, index) => {
            // Debug(show_item, ids[index], show_item === ids[index]);
            return show_item === ids[index];
          });
          // Debug("showShimple", result, showIds.join(","), ids.join(","), ids_same, ids.length, showIds.length);
          if (ids.length !== showIds.length || !ids_same) {
            const check = runFormFunction(params.formFunction, params.name, "getLoading");
            if (!check.result) {
              runFormFunction(params.formFunction, params.name, "setLoading", true);
              setTimeout(() => {
                const check = runFormFunction(params.formFunction, params.name, "getLoading");
                if (check.result) {
                  runFormFunction(params.formFunction, params.name, "setLoading", false);
                }
              }, dc.setTimeoutDelay * 5);
            }
          }
        }
        // Debug("checkResult", result, showIds.join(","), ids.join(","));
      }
      // Debug(codeList, result, resultMap, Object.keys(resultMap).length);
      return { result, ids: showIds };
    }
  }
  return { result: dc.tempSunburstData, ids: [] };
};
type FlavorWheelItemProp = FlavorWheelProp & {
  convertData: any;
  locale?: string;
  form?: FormInstance;
  formFunction?: Record<string, Record<string, Function>>;
  name?: string;
  target?: string;
  inputMode?: string;
  viewMode?: boolean;
  isMobile?: boolean;
  loading?: boolean;
  showSimple?: boolean;
  wheelRotate?: string | number;
  onChange?: ((event: any) => void) | undefined;
};
const wheelItemClick = (params: FlavorWheelItemProp, data: ComputedDatum<any>) => {
  if (!params.loading) {
    const target = params.target;
    if (target && params.form && params.form.getFieldInstance(target)) {
      const value = data.id;
      const values = params.form.getFieldValue(target);
      if (values instanceof Array) {
        if (values.includes(value)) {
          params.form.setFieldValue(
            target,
            values.filter((item) => item !== value),
          );
        } else {
          params.form.setFieldValue(target, [...values, value]);
        }
      } else {
        params.form.setFieldValue(target, [value]);
      }
      if (params.onChange) {
        params.onChange({ event, data });
      }
      runFormFunction(params.formFunction, params.name, "changeData");
    }
  }
};
export function FlavorWheelItem(params: FlavorWheelItemProp) {
  const colorAlpha = params.inputMode === "view" ? "55" : "67";
  const colorAlphaSub = params.inputMode === "view" ? "cc" : "cc";
  const reverse = false;
  const marginPx = (params.locale === enLocale ? 70 : 50) * (params.isMobile ? 0.5 : 1);
  const margin = {
    top: marginPx + (params.locale === enLocale ? 0 : 0),
    bottom: marginPx + 15 + (params.locale === enLocale ? -15 : 0),
    left: marginPx + 30 + (params.locale === enLocale ? 3 : 0),
    right: marginPx + 30 + (params.locale === enLocale ? 20 : 0),
  };
  const basefontSize = { normal: { check: 1, subcheck: 0.8, none: 0.6 }, rotate: { check: 0.8, subcheck: 0.7, none: 0.6 } };
  //checkRotate ? (check ? 15 : subcheck ? 9.5 : 8) : check ? 15 : subcheck ? 12 : 8.5;
  const running = useRef(0);
  const chartRotate =
    typeof params.wheelRotate === "string" ? Number(params.form?.getFieldValue(params.wheelRotate)) : (params.wheelRotate ?? 0);
  const CustomArcLabelLayer = (props: SunburstCustomLayerProps<any>) => {
    Debug("chartRotate", chartRotate, params.wheelRotate);
    return (
      <g transform={`translate(${props.centerX}, ${props.centerY}) rotate(${chartRotate})`}>
        {props.nodes.map((d) => {
          const check = d.data.check;
          const subcheck = d.data.subCheck;

          const checkRotate = d.data.level >= 2 && (check ? d.percentage < 7 : d.percentage < 2.5); //d.datum.data.level > 2 || (d.datum.data.level === 2 && d.datum.percentage < 2.5);

          const returnCheck =
            params.inputMode !== "view" && !params.viewMode
              ? !params.loading || 1 === 1
                ? true
                : d.data.level <= 2
              : d.data.level <= 1 || check || subcheck || d.percentage >= 3;

          const hasChild = d.data.children !== undefined;

          const lastCheck = check && checkRotate; //d.datum.data.level >= 2;
          const fill = returnCheck
            ? reverse
              ? check
                ? "var(--ant-color-text)"
                : "#ffffff"
              : check
                ? lastCheck && !hasChild
                  ? "var(--ant-color-text)"
                  : "#ffffff"
                : "var(--ant-color-text-quaternary)"
            : "#ffffff00";
          const stroke = returnCheck
            ? reverse
              ? check
                ? "var(--ant-color-text)"
                : undefined
              : check
                ? !lastCheck
                  ? "var(--ant-color-text)"
                  : "#ffffff"
                : undefined
            : "#ffffff00";

          const fontSizeRem =
            (!checkRotate
              ? check
                ? basefontSize.normal.check
                : subcheck
                  ? basefontSize.normal.subcheck
                  : basefontSize.normal.none
              : check
                ? basefontSize.rotate.check
                : subcheck
                  ? basefontSize.rotate.subcheck
                  : basefontSize.rotate.none) * (params.isMobile ? 0.7 : 1);
          const x = (checkRotate ? (lastCheck && !reverse && !hasChild ? 21 : -13) : 0) * (params.isMobile ? 0.5 : 1);
          const style: CSSProperties = {
            fontWeight: check || subcheck ? "900" : "normal", //d.datum.data.check ? "bold" : "normal",
            fontSize: fontSizeRem + "rem",
            strokeWidth: reverse ? (check ? "0.15px" : undefined) : check ? "0.15px" : undefined,
            cursor: lastCheck ? "pointer" : undefined,
            pointerEvents: lastCheck ? undefined : "none",
          };
          const labels = (checkRotate ? (d.data.label ?? "") : (d.data.label ?? "").replace(/[/]/g, "/\r\n")).split("\r\n");
          const dy = labels.length > 1 ? (labels.length - 1) * (fontSizeRem * 0.5) - (checkSafari ? fontSizeRem * 0.4 : 0) : undefined;

          const arc = d.arc;
          const angle = (arc.startAngle + arc.endAngle) / 2;
          const rotate = (angle * 180) / Math.PI - 90;
          const radius = arc.innerRadius + (arc.outerRadius - arc.innerRadius) / 2;
          const textAnchor = checkRotate ? "start" : "middle";
          const dominantBaseline = checkRotate ? "central" : "central";

          return (
            <text
              key={d.id}
              transform={`rotate(${rotate}) translate(${radius},0)` + (checkRotate ? `` : `rotate(90)`)}
              textAnchor={textAnchor}
              dominantBaseline={dominantBaseline}
              style={style}
              x={x}
              fill={fill}
              stroke={stroke}
              onClick={lastCheck ? (event) => wheelItemClick(params, d) : undefined}
            >
              {labels.length < 2
                ? d.data.label
                : labels.map((label: any, index: number) => {
                    return (
                      <tspan key={d.id + "_" + index} x={dy ? 0 : undefined} y={dy ? (fontSizeRem * 1 * index - dy) * 16 : undefined}>
                        {label}
                      </tspan>
                    );
                  })}
            </text>
          );
        })}
      </g>
    );
  };

  const CustomAnimatedLayer = () => {
    const props = useSpring({
      from: { opacity: 0 },
      to: { opacity: 1 },
      onStart: () => {
        running.current += 1;
      },
      onRest: () => {
        running.current -= 1;
        // console.log("모션 끝 : " + running.current);
      },
    });

    return <animated.g style={props}>{/* ... */}</animated.g>;
  };
  const prevShowSimple = usePrevious(params.showSimple);

  Debug("check show simple", { loading: params.loading, showSimple: params.showSimple, prevShowSimple });

  return (
    <ResponsiveSunburst
      theme={nivoTheme}
      //valueFormat=">-.1f"
      data={params.convertData.result}
      // data={[]}
      margin={margin}
      id="name"
      value="loc"
      cornerRadius={7}
      // borderColor={{ theme: "background" }}
      // colors={{ scheme: "nivo" }}
      inheritColorFromParent={false}
      // motionConfig={{ duration: dc.setTimeoutDelay * 3 }}
      colors={(d) => {
        //Debug(d);
        return reverse
          ? d.data.check
            ? d.data.color
            : d.data.subCheck
              ? d.data.color + colorAlphaSub
              : d.data.color + colorAlpha
          : d.data.check
            ? d.data.color
            : d.data.subCheck
              ? d.data.color + colorAlphaSub
              : d.data.color + colorAlpha;
      }}
      borderWidth={3}
      // borderColor={(d) => {
      //   // Debug(d);
      //   return d.data.check || d.data.subCheck ? "#ffffff" : "#ffffff";
      // }}
      enableArcLabels={false}
      layers={["arcs", CustomArcLabelLayer, CustomAnimatedLayer]}
      // layers={[
      //   "arcs",
      //   "arcLabels",
      //   () => {
      //     Debug("last layers", params.loading);
      //     return <></>;
      //   },
      // ]}
      arcLabel={(d) => {
        // Debug(d);
        return d.data.label;
      }}
      // arcLabelsRadiusOffset={0.1}

      arcLabelsComponent={
        params.loading && prevShowSimple !== params.showSimple
          ? undefined
          : (d) => {
              // Debug(d.style.transform.get());
              // if (d.datum.data.check) {
              //   Debug(d);
              // }
              const check = d.datum.data.check;
              const subcheck = d.datum.data.subCheck;

              //check || !checkRotate || d.datum.data.level <= 2 || params.inputMode !== "view"
              const returnCheck =
                params.inputMode !== "view" && !params.viewMode
                  ? !params.loading || 1 === 1
                    ? true
                    : d.datum.data.level <= 2
                  : d.datum.data.level <= 1 || check || subcheck || d.datum.percentage >= 3;

              if (returnCheck || 1 == 1) {
                const checkRotate = d.datum.data.level >= 2 && d.datum.percentage < 2.5; //d.datum.data.level > 2 || (d.datum.data.level === 2 && d.datum.percentage < 2.5);
                const hasChild = d.datum.data.children !== undefined;

                const lastCheck = check && checkRotate; //d.datum.data.level >= 2;
                const labelAngle = (d.datum.arc.endAngle - d.datum.arc.startAngle) / 2 + d.datum.arc.startAngle;
                const rotate =
                  d.style.transform.get() + " rotate(" + Math.round(labelAngle * 180 + (checkRotate ? Math.PI * -90 : 0)) / Math.PI + ")";
                const textAnchor = checkRotate ? "start" : "middle";
                const dominantBaseline = checkRotate ? "central" : "central";
                const fill = returnCheck
                  ? reverse
                    ? check
                      ? "var(--ant-color-text)"
                      : "#ffffff"
                    : check
                      ? lastCheck && !hasChild
                        ? "var(--ant-color-text)"
                        : "#ffffff"
                      : "var(--ant-color-text-quaternary)"
                  : "#ffffff00";
                const stroke = returnCheck
                  ? reverse
                    ? check
                      ? "var(--ant-color-text)"
                      : undefined
                    : check
                      ? !lastCheck
                        ? "var(--ant-color-text)"
                        : "#ffffff"
                      : undefined
                  : "#ffffff00";

                const fontSizeRem =
                  (!checkRotate
                    ? check
                      ? basefontSize.normal.check
                      : subcheck
                        ? basefontSize.normal.subcheck
                        : basefontSize.normal.none
                    : check
                      ? basefontSize.rotate.check
                      : subcheck
                        ? basefontSize.rotate.subcheck
                        : basefontSize.rotate.none) * (params.isMobile ? 0.7 : 1);
                const x = (checkRotate ? (lastCheck && !reverse && !hasChild ? 21 : -13) : 0) * (params.isMobile ? 0.5 : 1);
                const style = {
                  fontWeight: check || subcheck ? "900" : "normal", //d.datum.data.check ? "bold" : "normal",
                  fontSize: fontSizeRem + "rem",
                  // strokeWidth: check ? "0.1px" : undefined,
                  strokeWidth: reverse ? (check ? "0.15px" : undefined) : check ? "0.15px" : undefined,
                  // textShadow: check
                  //   ? lastCheck
                  //     ? "#888888ee 1px 1px 2px"
                  //     : "#555555 1.7px 1.7px 2px"
                  //   : subcheck
                  //     ? lastCheck
                  //       ? "#888888ee 1px 1px 1px"
                  //       : "#555555f0 1.7px 1.7px 1px"
                  //     : undefined,
                };
                // if (labels.length > 1) {
                //   Debug(labels);
                // }
                const labels =
                  // checkRotate ? d.label ?? "" : (d.label.indexOf("/") > 0 ? d.label + "/" + d.label : d.label ?? "").replace(/[/]/g, "/\r\n")
                  (checkRotate ? (d.label ?? "") : (d.label ?? "").replace(/[/]/g, "/\r\n")).split("\r\n");
                const dy =
                  labels.length > 1 ? (labels.length - 1) * (fontSizeRem * 0.5) - (checkSafari ? fontSizeRem * 0.4 : 0) : undefined;
                //style={{ pointerEvents: "none" }}
                return (
                  <g
                    transform={rotate}
                    opacity={params.loading ? 0.7 : 1}
                    style={lastCheck ? { cursor: "pointer" } : { pointerEvents: "none" }}
                  >
                    <text
                      className="flavorWheelText"
                      textAnchor={textAnchor}
                      dominantBaseline={dominantBaseline}
                      // x={checkRotate ? -13 : 0}
                      // fill={check ? "white" : "var(--ant-color-text-quaternary)"}
                      // stroke={check ? "var(--ant-color-text)" : undefined}
                      x={x}
                      fill={fill}
                      stroke={stroke}
                      style={style}
                      onClick={
                        lastCheck
                          ? (event) => {
                              wheelItemClick(params, d.datum);
                            }
                          : undefined
                      }
                    >
                      {labels.length < 2
                        ? d.label
                        : labels.map((label, index) => {
                            return (
                              <tspan
                                key={d.datum.id + "_" + index}
                                x={dy ? 0 : undefined}
                                y={dy ? (fontSizeRem * 1 * index - dy) * 16 : undefined}
                              >
                                {label}
                              </tspan>
                            );
                          })}
                      {/* {d.label} */}
                    </text>
                  </g>
                );
              } else {
                return <></>;
              }
              // Debug(d.style.transform.calc());
            }
      }
      // arcLabelsSkipAngle={params.loading ? 10 : undefined}
      // animate={false}
      arcLabelsTextColor={
        !params.loading
          ? {
              from: "color",
              modifiers: [["darker", 1.4]],
            }
          : (d) => {
              if (d.data.level < 3) {
                if (d.data.check) {
                  return "#ffffffcd";
                } else {
                  return "var(--ant-color-text-quaternary)";
                }
              } else {
                if (d.data.check) {
                  return "var(--ant-color-text)";
                } else {
                  return "#ffffff00";
                }
              }
            }
      }
      tooltip={
        params.inputMode !== "view" && !params.viewMode
          ? (props) => <></>
          : (props) => {
              // Debug(props);
              const check = props.data.check;
              const subcheck = props.data.subCheck;
              return (
                <div style={{ ...dc.chartCoverDivstyle, borderRadius: "0.75rem", boxShadow: "rgba(0, 0, 0, 0.25) 3px 3px 5px" }}>
                  <div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: "3px 5px" }}>
                            <span
                              style={{
                                display: "block",
                                width: check ? "1.2rem" : subcheck ? "1rem" : "0.75rem",
                                height: check ? "1.2rem" : subcheck ? "1rem" : "0.75rem",
                                background: props.data.color,
                                marginRight: "0.5rem",
                              }}
                            ></span>
                          </td>
                          <td style={{ padding: "3px 5px" }} colSpan={2}>
                            <span
                              style={{
                                fontWeight: check ? "bold" : "normal",
                                fontSize: check ? "1.2rem" : subcheck ? "1rem" : "0.75rem",
                                textShadow: check || subcheck ? "#aaaaaa 1px 1px 4px" : undefined,
                              }}
                            >
                              {props.data.label ?? ""}
                              {props.data.subLabel !== undefined ? " - " + props.data.subLabel : ""}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            }
      }
      // transitionMode="middleAngle"
      onClick={(data, event) => {
        // Debug(data, event);
        wheelItemClick(params, data);
      }}
      {...params}
    />
  );
}
const getShowSimple = (form?: FormInstance<any>, showSimple?: string | boolean | undefined, values?: any): any => {
  const result =
    typeof showSimple === "string"
      ? form?.getFieldInstance(showSimple)
        ? form?.getFieldValue(showSimple)
        : values[showSimple]
      : showSimple;
  return result;
};

export default function DataFlavorWheel(params: DataInputParams) {
  const t = useTranslations(params.transitionGroup ?? "contents");
  const prev = params.prev ? GetDataInputElement(params.prev, false, params.values, t) : undefined;
  const next = params.next ? GetDataInputElement(params.next, true, params.values, t) : undefined;
  const [changeLabel, setChangeLabel] = useState<boolean>(true);
  const [animate, setAnimate] = useState<boolean>(true);
  const [isMobile, setMobile] = useState<boolean | undefined>(checkMobile());
  const [loading, setLoading] = useState<boolean>(false);

  const DataLabel: DataElement =
    typeof params.label === "string" || params.label === undefined
      ? { element: params.label ?? (params.labelNamePrev ?? "") + params.name }
      : params.label;

  const [data, setData] = useState<Record<string, any>>(() => {
    const data = dataConverter(t, params, []);
    // Debug("init", data);
    return data;
  });

  if (params.formFunction && params.name) {
    if (!params.formFunction[params.name]) {
      params.formFunction[params.name] = {};
    }
    params.formFunction[params.name]["getLoading"] = () => {
      return loading;
    };
    params.formFunction[params.name]["setLoading"] = (loading_s: boolean) => {
      Debug("start loading", loading_s, loading);
      if (loading_s !== loading) {
        setTimeout(() => {
          setLoading(loading_s);
        }, dc.setTimeoutShort);
      }
    };

    params.formFunction[params.name]["changeData"] = () => {
      // Debug("formFunction changeData", data.ids);
      setTimeout(() => {
        setMobile(checkMobile());
        setData(dataConverter(t, params, data.ids ?? []));
      }, dc.setTimeoutShort);
    };
    params.formFunction[params.name]["changeLabel"] = () => {
      setChangeLabel(true);
    };
  }

  if (params.resetFunction && params.name) {
    params.resetFunction[params.name] = () => {
      Debug("resetFunction changeData");
      setMobile(checkMobile());
      setTimeout(() => {
        setData(dataConverter(t, params, data.ids ?? []));
        runFormFunction(params.formFunction, params.name, "setLoading", true);
      }, dc.setTimeoutDelay);
      setTimeout(() => {
        const check = runFormFunction(params.formFunction, params.name, "getLoading");
        if (check.result) {
          runFormFunction(params.formFunction, params.name, "setLoading", false);
        }
      }, dc.setTimeoutDelay * 5);
    };
  }
  const [showSimple, setShowSimple] = useState(getShowSimple(params.form, params.flavorWheelProp?.showSimple, params.values));
  useEffect(() => {
    setShowSimple(getShowSimple(params.form, params.flavorWheelProp?.showSimple, params.values));
  }, [loading, params.flavorWheelProp?.showSimple, params.form, params.values]);
  // useEffect(() => {
  //   Debug("params changeData");
  //   const changeData = dataConverter(t, params, []);
  //   setMobile(checkMobile());
  //   setData(changeData);
  // }, [params, t]);
  // const gridLabel: GridLabelComponent = (e) => {
  //   //const label = indexInfo.fieldLabel[e.id] ?? e.id;
  //   return (
  //     <g transform={`translate(${e.x},${e.y})`} dominantBaseline="central" textAnchor="middle">
  //       <text fill="var(--gray-a11)">{e.id}</text>
  //     </g>
  //   );
  // };
  const defaultClassName = "flex-col h-[25rem] sm:h-[40rem] sm:w-[40rem] w-full"; //isMobile ?  : "flex-col h-[40rem] w-full";

  const ta = useTranslations("alert");
  const layoutRef = useRef<HTMLDivElement>(null);
  const alertContext = useContext(AlertContext);
  const GetButtons = () => {
    return (
      <Flex className={cn(" w-full text-right relative right-2 z-10 chart-tool screenAnimation h-0 flex-row-reverse")}>
        {params.imageDownload ? (
          <Button
            className={cn("ml-2")}
            icon={<Icon iconType="" iconName="DownloadSimple" weight="bold" />}
            shape="circle"
            onClick={() => {
              exportChartAsPNG(ta, alertContext, layoutRef.current, "FlavorWheel", params.imageDownload);
            }}
          ></Button>
        ) : undefined}
      </Flex>
    );
  };

  if (params.asChild ?? false) {
    return (
      <>
        {(params.labelUse ?? true) && (DataLabel.display ?? true) ? <GetDataLabel {...params} /> : ""}
        <Flex ref={layoutRef} className={cn("data-chart FlavorWheel", params.inputClassName ?? defaultClassName)}>
          {GetButtons()}
          <FlavorWheelItem
            {...params.flavorWheelProp}
            convertData={data}
            locale={params.locale}
            formFunction={params.formFunction}
            name={params.name}
            inputMode={params.inputMode}
            isMobile={isMobile}
            showSimple={showSimple}
          />
        </Flex>
      </>
    );
  } else {
    const useLabel = (params.labelUse ?? true) && (DataLabel.display ?? true);
    const mergeValueStyle = useLabel ? params.valueStyle : { ...params.valueStyle, gridColumn: "1 / span 2" };
    // params.onChange;

    return (
      <>
        <DataList.Item
          className={cn(params.itemClassName, params.nextItemHidden ? "next-item-hidden" : "", (params.show ?? true) ? "" : "hidden")}
        >
          {params.loading ? (
            <AntSkeleton.Input active={true} block={true} />
          ) : useLabel ? (
            <DataList.Label className={params.labelClassName} style={params.labelStyle}>
              <GetDataLabel {...params} />
            </DataList.Label>
          ) : (
            ""
          )}
          <DataList.Value className={params.valueClassName} style={params.loading ? {} : mergeValueStyle}>
            {params.loading ? (
              <AntSkeleton.Input active={true} block={true} className={defaultClassName} />
            ) : (
              <>
                {prev}
                <Flex ref={layoutRef} className={cn("data-chart FlavorWheel", params.inputClassName ?? defaultClassName)}>
                  {GetButtons()}
                  <FlavorWheelItem
                    {...params.flavorWheelProp}
                    convertData={data}
                    locale={params.locale}
                    form={params.form}
                    formFunction={params.formFunction}
                    name={params.name}
                    inputMode={params.inputMode}
                    isMobile={isMobile}
                    loading={loading}
                    showSimple={showSimple}
                    onChange={params.onChange}
                  />
                </Flex>
                {next}
              </>
            )}
          </DataList.Value>
        </DataList.Item>
      </>
    );
  }
}
